import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, NgZone, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild } from '@angular/core'
import { CURRENT_STATE_PROJECT_KEY, EDGE_TYPE, LINQSET_TYPE, Linqset, LinqsetEdge, LinqsetNode, MODEL_TYPE, NODE_NAME_MAX, NODE_SUPERTYPE, NODE_TYPE, ORGANISATION_MODEL_KEY, canConnect, cloneLinqsetNode, isModelNode, isSupplyChain } from '../../common/linqset-model';
import { createLinqset } from '../../common/model-processing';
import {v4 as UUID, v4} from 'uuid';
import { LinqModelToCanvasService } from '../../model-services/linqmodel.to.canvas.service';
import { EdgeService } from '../../model-services/edge.service';
import { LabelService } from '../../model-services/label.service';
import { NodeService } from '../../model-services/node.service';
import { LABEL_TYPES } from '../../model-services/model-item-enums';
import { canDeleteModel, createInsightsFilter, modelChangedInProject, ModelDetails } from '../../common/organisation';
import { UserService } from '../../user/user.service';
import { NODE_MOVE_OPTION, PreferencesChange, USER_ROLE, changedDrawingPreferences } from '../../common/user';
import { Subscription } from 'rxjs';
import { LinqsetRepositoryService } from '../../model-services/linqset-repository.service';
import { GroupNodeTag, LinqsetNodeVisualisation, createModelEdge } from '../model/graph-model';
import { getInsightsWithoutCreate } from '../../common/linqset-model';
import { DEFAULT_CURRENCY, defaultCapacityHours, findTagForOrganisation, getCurrency } from '../../common/organisation';
import { groupNodeTip, linqsetNodeTip, modelNodeTip, NodeTip, renderNodeTip, TipFormat, tipFormat } from '../node-tooltip';
import { MatSnackBar } from '@angular/material/snack-bar';
import { OrganisationService } from '../../organisation/organisation.service';
import { GraphService } from '../graph.service';
import { GraphProcessingService } from '../../model-services/graph-processing.service';
import { AdjacencyTypes, Command, CreateEdgeInputMode, EventRecognizers, FreeNodePortLocationModel, GraphClipboard, GraphClipboardEventArgs, GraphComponent, GraphEditorInputMode, GraphInputMode, GraphItemTypes, IEdge, IGraph, IInputModeContext, ILabel, IModelItem, INode, InputModeEventArgs, IPort, IPortCandidate, ItemEventArgs, ItemsEventArgs, KeyEventArgs, LabelEditingEventArgs, LabelTextValidatingEventArgs, ModifierKeys, MouseWheelBehaviors, PreventViewportInteractionPolicy, Rect, Size, SizeChangedEventArgs, NodeAlignmentPolicy, NodeDropInputMode, Point, PointerButtons, PointerEventArgs, PopulateItemContextMenuEventArgs, PortCandidate, PortDropInputMode, SelectionEventArgs, TimeSpan } from '@yfiles/yfiles';
import { ExceptionAndLoggingService } from '../../linq-utils/logging/exception-logging-service';
import { FilterService } from '../../model-services/filter.service';
import { FUNCTION, FunctionService } from '../../model-services/function.service';
import { LensInputMode } from '../LensInputMode';
import { GroupService } from '../../model-services/group.service';

const COMPONENT = "Graph component"

@Component({
    selector: 'app-graph-component',
    templateUrl: './graph-component.component.html',
    styleUrls: ['./graph-component.component.css'],
    standalone: false
})
export class GraphComponentComponent implements AfterViewInit, OnDestroy, OnChanges {
    @Input() linqset:Linqset
    @Input() modelType:MODEL_TYPE
    @Input() readOnly:boolean
    @Input() contextMenu:boolean = true     // whether right-clicking offers a menu, such as editing or deleting a model
    @Input() trigger:any
    @Input() focusToInteract:boolean = false   // when embedded in a scrolling page, only zoom/pan once the canvas is clicked so the wheel scrolls the page

    @ViewChild('graphComponentRef') graphComponentRef: ElementRef | undefined

    @Output() itemSelectedEvent = new EventEmitter();
    @Output() itemDoubleClickedEvent = new EventEmitter<IModelItem>();
    @Output() graphReady = new EventEmitter<Linqset>()     // the graph has been built for this linqset

    graphComponent: GraphComponent

    latestModelKey:string         // remember the latest model key to check for change
	mouseCurrentLocation:Point = null;
    escapePressed:boolean = false

    clearingGraph:boolean = true
    preventProcessGraph = false
    lensInputMode: LensInputMode = undefined
    private moveDefaults:{selectedEnabled:boolean, unselectedEnabled:boolean, startOverCandidateOnly:boolean, beginRecognizer:(evt:any, sender:any) => boolean, hoverRecognizer:(evt:any, sender:any) => boolean, priority:number}|undefined
    private preferencesSubscription:Subscription|undefined

    constructor(private edgeService:EdgeService,
        private graphProcessingService:GraphProcessingService,
        private graphService:GraphService,
        private organisationService:OrganisationService,
        private userService:UserService,
        private labelService: LabelService,
        private repoService:LinqsetRepositoryService,
        private filterService:FilterService,
        private functionService:FunctionService,
        private nodeService: NodeService,
        private exceptionHandler: ExceptionAndLoggingService,
        private groupService:GroupService,
        private _zone: NgZone,
        private snackBar:MatSnackBar,
        private changeDetector:ChangeDetectorRef,
        private linqModelService: LinqModelToCanvasService) {

    }

    ngOnChanges(changes: SimpleChanges): void {
        // a new model type is rebuilt in full too: grouping and ports depend on it
        if (this.linqset && (changes["linqset"] || changes["readOnly"] || changes["trigger"] || changes["modelType"])) this.newLinqset()
    }

    ngAfterViewInit(): void {
        if (!this.graphComponent && this.graphComponentRef) {
            this._zone.runOutsideAngular(() => {
                this.graphComponent = new GraphComponent(this.graphComponentRef.nativeElement)
                if (this.focusToInteract) this.graphComponent.preventViewportInteraction = PreventViewportInteractionPolicy.UNFOCUSED
                ;(window as any).graphComponent = this.graphComponent           // set as a property of window for testing purposes
                this.setupGraph()
            })
            this.preferencesSubscription = this.userService.preferencesChangedEvent.subscribe(change => this.applyPreferences(change))
        }
        if (this.linqset) this.newLinqset()
    }

    ngOnDestroy(): void {
        this.preferencesSubscription?.unsubscribe()
        if (this.graphComponent) {
            this.removeListeners()
            this.graphComponent.cleanUp()
            this.graphComponent = null
        }
    }

    public async newLinqset():Promise<void> {
        if (!this.linqset || !this.graphComponent) return

        const newModel = this.latestModelKey !== this.linqset.modelKey

        // read-only canvases still take input, for panning, selecting and double-clicking to open things;
        // what would change the model is switched off
        const inputMode = this.graphComponent.inputMode as GraphEditorInputMode
        inputMode.enabled = !this.readOnly
        inputMode.createEdgeInputMode.enabled = !this.readOnly
        inputMode.allowClipboardOperations = !this.readOnly
        inputMode.allowGroupingOperations = !this.readOnly
        inputMode.allowUndoOperations = !this.readOnly
        this.graphService.createPortHandler(this.graphComponent, this.linqset)
        this.latestModelKey = this.linqset.modelKey
        await this.resetGraph(newModel)
        if (this.linqset.type === LINQSET_TYPE.NODE_FILTER || this.linqset.type === LINQSET_TYPE.CAPTURE || this.linqset.type === LINQSET_TYPE.OUTCOME) {
            this.groupService.groupNodesByModel(this.graphComponent.graph, this.linqset, this.linqset.type === LINQSET_TYPE.NODE_FILTER)
            this.graphService.runLayoutForLinqset(this.graphComponent, this.linqset)
        }
        this.graphReady.emit(this.linqset)
    }

    public getSelectedNodes() {
        return this.graphComponent.selection.filter(n => n instanceof INode).toArray()
    }

    public async resetGraph(newLinqset:boolean) {
        if (!this.linqset) return

        const lastSelected = this.graphComponent.selection.last()
        this.itemSelectedEvent.emit(null)

        this._zone.runOutsideAngular(async () => {

            this.clearingGraph = true
            try {
                this.graphComponent.graph?.undoEngine?.clear()
                //this.filterService.clearFilter(this.graphComponent, this.linqset)
                if (this.readOnly) this.graphService.switchToPanMode(this.graphComponent)
                //else this.graphService.switchToSelectMode(this.graphComponent)

                await this.linqModelService.createGraph(this.organisationService.organisation, this.graphComponent, this.linqset, this.modelType)

            }catch(err) {
                this.exceptionHandler.handleCaughtError(err, COMPONENT, "Reset graph", "Exception while creating graph", this.userService.getUser(), this.organisationService.organisation, this.linqset?.model)
            }finally {
                this.clearingGraph = false
                this.changeDetector.detectChanges()
            }

            // a node asked for by a link is selected by the model page once loading settles
            if (!newLinqset && lastSelected) {
                this.graphService.zoomToModelItem(this.graphComponent, lastSelected)
            }

        })
    }

    private setupGraph(): GraphEditorInputMode {
        this.graphService.initializeGrouping(this.graphComponent)

        const inputMode = this.initializeInputModes()
        this.graphComponent.inputMode = inputMode

        this.setupClipboard()
        this.initializeTooltips(inputMode)
        this.setFreehandSelection()
        this.initializeStyling()

        this.configureContextMenu()

        this.createListeners()
        return inputMode;
    }

    public toggleMagnifyingGlass() {
        const inputMode = this.graphComponent.inputMode as GraphEditorInputMode

        if (this.lensInputMode) {
            inputMode.remove(this.lensInputMode)
            this.lensInputMode = undefined
        }else {
            this.lensInputMode = new LensInputMode()
            inputMode.add(this.lensInputMode)

            this.lensInputMode.zoomFactor = 1
            this.graphComponent.zoom = 0.5
        }
    }

    public addExternalNode(node:LinqsetNode, externalModel:ModelDetails) {
        // create a visualisation of the model that is being connected
        const modelNode = this.nodeService.createModelNode(externalModel, this.linqset.workspaceKey, this.graphComponent, this.mouseCurrentLocation)

        // add the node to this model
        const location = new Point(modelNode.layout.x, modelNode.layout.y+100)
        const externalNode = this.nodeService.createNodeAt(this.linqset, this.graphComponent.graph, node, v4(), location)

        const edgeStyle = this.edgeService.getStyleForEdge(EDGE_TYPE.MODEL)
        this.graphComponent.graph.createEdge(modelNode, externalNode, edgeStyle)
        this.runLayout()
    }

    public toggleGroupNode(node:INode) {
        const foldingView = this.graphComponent.graph.foldingView

        if (foldingView.isExpanded(node)){
            foldingView.collapse(node)
        }else foldingView.expand(node)

        this.runLayout()
    }

    public toggleGroups(expand:boolean) {
        this.clearingGraph = true
        try {
            const foldingView = this.graphComponent.graph.foldingView
            const nodes = this.graphComponent.graph.nodes.filter(n => {
                const master = foldingView.getMasterItem(n)
                if (master && foldingView.manager.masterGraph.isGroupNode(master)) {
                    const expanded = foldingView.isExpanded(n)
                    if (expand) return !expanded
                    else return expanded
                }

                return false
            }).toArray()
            for (const n of nodes) {
                if (expand) foldingView.expand(n)
                else foldingView.collapse(n)
            }
        }finally {
            this.clearingGraph = false
            this.runLayout()
        }
    }

    // every listener added in createListeners is recorded here so removeListeners takes off exactly the same
    // function references (a fresh .bind(this) or inline arrow can never be removed)
    private registeredListeners:{target:any, type:string, listener:any}[] = []

    private listen(target:any, type:string, listener:any) {
        target.addEventListener(type, listener)
        this.registeredListeners.push({target, type, listener})
    }

    private removeListeners() {
        this.registeredListeners.forEach(({target, type, listener}) => target.removeEventListener(type, listener))
        this.registeredListeners = []
    }

    private createListeners() {
        const inputMode = <GraphEditorInputMode>this.graphComponent.inputMode
        const foldingView = this.graphComponent.graph.foldingView
        const graph = foldingView.manager.masterGraph

        this.listen(this.graphComponent, 'pointer-down', this.handleMouseClick.bind(this))
        this.listen(this.graphComponent, 'wheel', this.handleMouseWheel.bind(this))
        this.listen(this.graphComponent, 'size-changed', this.handleSizeChanged)

        this.listen(inputMode.navigationInputMode, 'group-expanded', this.handleGroupExpanded.bind(this))
        this.listen(inputMode.navigationInputMode, 'group-collapsed', this.handleGroupCollapsed.bind(this))

        this.listen(inputMode, 'item-double-clicked', this.handleItemDoubleClick.bind(this))
        this.listen(inputMode, 'item-left-clicked', this.handleItemLeftClicked.bind(this))
        this.listen(inputMode, 'multi-selection-finished', this.handleAddMultiSelectionFinished.bind(this))

        // Label editing
        this.listen(this.graphComponent, 'key-down', this.handleKeyDown.bind(this))
        this.listen(inputMode.editLabelInputMode, 'query-label-editing', this.handleLabelStartEditing.bind(this))
        this.listen(inputMode.editLabelInputMode, 'validate-label-text', this.validateLabel.bind(this))
        this.listen(inputMode.editLabelInputMode, 'label-edited', this.handleLabelTextChanged.bind(this))

        this.listen(inputMode, 'deleting-selection', this.handleDeletingSelection.bind(this))

        this.listen(inputMode.nodeDropInputMode, 'drag-over', this.onDragOverHandler.bind(this))
        this.listen(inputMode, 'deleted-selection', this.handleDeletedSelection.bind(this))

        this.listen(graph, 'edge-created', this.handleEdgeCreated.bind(this))
        this.listen(graph, 'edge-removed', this.handleEdgeRemoved.bind(this))
        this.listen(graph, 'node-removed', this.handleNodeRemoved.bind(this))
        this.listen(graph, 'node-created', this.handleNodeCreated.bind(this))

        this.listen(graph, 'edge-ports-changed', this.handleEdgePortsChanged.bind(this))

        this.listen(inputMode.createEdgeInputMode, 'gesture-started', this.handleGestureStarted.bind(this))
        this.listen(inputMode.createEdgeInputMode, 'gesture-canceling', this.handleGestureCanceled.bind(this))

        // clipboard
        this.listen(this.graphComponent.clipboard, 'items-copying', this.filterCopiedItems.bind(this))
        this.listen(this.graphComponent.clipboard, 'items-cutting', this.filterCopiedItems.bind(this))
        this.listen(this.graphComponent.clipboard, 'items-pasting', this.itemsPasting.bind(this))

        this.listen(inputMode, 'items-pasted', () => this.graphService.reRouteEdges(this.graphComponent))

        this.listen(inputMode.moveSelectedItemsInputMode, 'drag-finished', () => this.graphService.reRouteEdges(this.graphComponent))
        this.listen(inputMode.moveUnselectedItemsInputMode, 'drag-finished', () => this.graphService.reRouteEdges(this.graphComponent))
        this.listen(inputMode.handleInputMode, 'drag-finished', () => this.graphService.reRouteEdges(this.graphComponent))

        inputMode.deletablePredicate = (item) => {
            if (this.isAllowedToDelete(item)) return true
            if (this.readOnly) this.snackBar.open("Cannot delete this node. Graph is read only.", "close")
            else this.snackBar.open("Cannot delete this node. Check relationships or use in other models.", "close")
            return false
        }

    }

    private handleKeyDown(args:KeyEventArgs, sender:GraphComponent) {

        if (args.key === 'Escape') this.graphService.switchToSelectMode(this.graphComponent)
        else {
            // typing on a node that has no name starts naming it; keys such as arrows, Tab or Delete are not names
            if (this.readOnly || args.altKey || args.ctrlKey || args.metaKey || args.key?.length !== 1 || !args.key.trim()) return
            const item = sender.currentItem

            if (item && (item instanceof INode) && item.tag?.node && (!item.labels || !item.labels.find(l => l.tag && l.tag.type ===LABEL_TYPES.NODE_NAME))) {

                // the key is the start of the name, so it isn't typed again: by yFiles' own editing on typing, which
                // adds what's typed to the label's text, or into the editor once it has opened
                args.preventDefault()
                const label = this.labelService.addLabelToNode(sender.graph, item, args.key, LABEL_TYPES.NODE_NAME)
                // the editor opens with the caret after the key rather than the key selected, so typing carries on
                const textEditor = (sender.inputMode as GraphEditorInputMode).editLabelInputMode.textEditorInputMode
                textEditor.selectContent = false
                sender.executeCommand(Command.EDIT_LABEL, label)
                textEditor.selectContent = true
                // we have to do this here because edited event not triggered immediately
                item.tag.node.name = args.key
                this.emitChangeEvent(item, {name:args.key})
            }
        }
    }

    // EDITING

    // only allow editing of node name
    private handleLabelStartEditing(evt:LabelEditingEventArgs, sender:GraphEditorInputMode) {
        if (!this.readOnly && evt.owner?.tag?.node && (!evt.label?.tag || evt.label.tag.type === LABEL_TYPES.NODE_NAME)) {
            if (!this.linqset.isProjectModel() || evt.owner?.tag?.node.projectKey === this.linqset.workspaceKey) return
        }
        evt.cancel = true
    }

    // restrict length of node name
    private validateLabel(evt:LabelTextValidatingEventArgs, sender:GraphEditorInputMode) {
        if (evt.label.tag.type === LABEL_TYPES.NODE_NAME && evt.newText.length > NODE_NAME_MAX) {
            evt.validatedText = null
        }
    }

    private handleLabelTextChanged(evt:ItemEventArgs<ILabel>, sender:GraphComponent) {
        const node = evt.item.owner as INode
        if (node.tag.model) this.linqset.model.name = evt.item.text
        else {
            if (!this.linqset.isOrganisationModel() && (node.tag.node.superType === NODE_SUPERTYPE.PEOPLE || node.tag.node.superType === NODE_SUPERTYPE.ROLE || node.tag.node.superType === NODE_SUPERTYPE.SYSTEM)) {
                // try and find existing node with same name
                const existingNode = this.findExistingNodeForName(this.linqset.nodes, node, evt.item.text) ||
                    this.findExistingNodeForName(this.repoService.organisationLinqset.nodes, node, evt.item.text)

                if (existingNode) this.changeExistingNode(node, existingNode)
                else {
                    node.tag.node.name = evt.item.text
                    this.emitChangeEvent(node, {name:evt.item.text})
                }
            }else {
                node.tag.node.name = evt.item.text
                this.emitChangeEvent(node, {name:evt.item.text})
            }
        }
    }

    // EDITING FINISHED

    // CLIPBOARD HANDLERS
    private filterCopiedItems(evt:GraphClipboardEventArgs, sender:GraphClipboard) {
        evt.items.removeAll(i => !i.tag?.edge && !i.tag?.node)
    }

    private itemsPasting(e:GraphClipboardEventArgs, sender:GraphClipboard) {
        e.items.forEach(i => {
            if (i instanceof INode) {
                this.replicatePastedNode(i)
            }else if (i instanceof IEdge) {
                this.replicatePastedEdge(i)
            }
        })
    }

    private replicatePastedNode(node:INode) {
        const lsNode:LinqsetNode = node.tag?.node
        if (lsNode) {
            if (isModelNode(lsNode.superType) || (lsNode.projectKey != CURRENT_STATE_PROJECT_KEY && lsNode.projectKey !== this.linqset.workspaceKey)) {
                // model node or organisation node created in a project so create a new node
                const copiedNode = cloneLinqsetNode(lsNode)
                node.tag = new LinqsetNodeVisualisation(copiedNode, UUID())
                copiedNode.modelKey = isModelNode(lsNode.superType)?this.linqset.modelKey:ORGANISATION_MODEL_KEY
                copiedNode.organisationKey = this.linqset.organisationKey
                copiedNode.projectKey = this.linqset.workspaceKey
            }else {
                // org node that is in current state so just reference it
                node.tag = new LinqsetNodeVisualisation(lsNode, UUID())
            }
        }
    }

    private replicatePastedEdge(edge:IEdge) {
        const lsEdge:LinqsetEdge = edge.tag?.edge

        if (lsEdge) {
            edge.tag = createModelEdge({...lsEdge, _key:UUID(), modelKey: this.linqset.modelKey, organisationKey:this.linqset.organisationKey,
                projectKey: this.linqset.workspaceKey, sourceKey:edge.sourceNode.tag.node._key, targetKey:edge.targetNode.tag.node._key,
                sourceVisualisationKey:edge.sourceNode.tag.visualisationKey, targetVisualisationKey:edge.targetNode.tag.visualisationKey})
            delete edge.tag.edge._id
            delete edge.tag.edge._rev
        }
    }

    // END CLIPBOARD HANDLERS

    private handleGroupExpanded(evt:any, sender:GraphComponent) {
        if (!this.clearingGraph) {
            this.runLayout()
        }
    }

    private handleGroupCollapsed(evt:any, sender:GraphComponent) {
        if (!this.clearingGraph) {
            this.runLayout()
        }
    }

    private handleItemDoubleClick(args:any, sender:GraphComponent) {
        // if not handled then we raise an event. The graph's events are listened to outside Angular, so it is raised
        // inside it, or the page wouldn't update until something else made it check
        if (!this.handleItemDoubleClicked(args.item))
            this._zone.run(() => this.itemDoubleClickedEvent.emit(args.item))
    }

    private handleItemLeftClicked(args:any, sender:GraphComponent) {
        this.graphComponent.currentItem = args.item
    }

    private handleAddMultiSelectionFinished(args:any, sender:GraphComponent) {
        this.itemSelectedEvent.emit(true)
    }

    private emitChangeEvent(node:INode, change:any) {
        this.graphProcessingService.createChangesEvent([node], change, true)
    }

    private handleDeletingSelection(evt:SelectionEventArgs<IModelItem>, sender:GraphComponent) {
        if (!this.readOnly) {
            this.clearingGraph = true
            const selection = evt.selection.filter(i => i.tag).toArray()
            if (selection.length > 0) {
                const result = this.processDeletedSelection(selection)
                if (result.length > 0) this.graphProcessingService.createDeletedItemsEvent(result)
            }
        }
    }

    private handleDeletedSelection(evt:any, sender:GraphComponent) {
        this.clearingGraph = false
    }
    private handleEdgeRemoved(evt:any, sender:GraphComponent) {
        if (!this.clearingGraph && !this.readOnly && evt.item.tag) {
            this.graphProcessingService.createDeletedItemsEvent([evt.item], this.preventProcessGraph)
        }
    }

    private handleNodeRemoved(evt:any, sender:GraphComponent) {
        if (!this.clearingGraph && !this.readOnly && evt.item.tag && evt.item.tag.node) {
            this.graphProcessingService.createDeletedItemsEvent([evt.item], this.preventProcessGraph)
        }

    }
    private handleNodeCreated(evt:any, sender:GraphComponent) {
        if (!this.clearingGraph && !this.readOnly && evt.item.tag && evt.item.tag.node) {
            evt.item.tag.includeInLayout = true
            this.graphProcessingService.createAddedItemsEvent([evt.item], this.preventProcessGraph)
        }
    }

    private handleEdgeCreated(evt:ItemEventArgs<IEdge>, sender:IGraph) {
        if (!this.clearingGraph && !this.readOnly && evt.item.tag) {
            this.graphProcessingService.createAddedItemsEvent([evt.item], this.preventProcessGraph)
        }
    }

    private handleEdgePortsChanged(evt:any, sender:GraphComponent) {
        if (!this.clearingGraph && !this.readOnly && evt.item.tag) {
            this.graphProcessingService.createEdgeChangesEvent([evt.item], this.preventProcessGraph)
        }
    }

    private handleGestureStarted(evt:any, sender:CreateEdgeInputMode) {
        if (!this.readOnly) this.graphService.edgeCreationListener(this.graphComponent, sender)
    }

    private handleGestureCanceled(evt:InputModeEventArgs, sender:CreateEdgeInputMode) {
        if (this.mouseCurrentLocation && !this.escapePressed) {
            this.graphService.edgeCanceledListener(this.graphComponent, this.linqset, this.mouseCurrentLocation, sender, evt)
        }
    }

    private isAllowedToDelete(item:IModelItem) {
        if (this.readOnly) return false

        if (item instanceof IEdge) {
            if (this.linqset.isOrganisationModel()) return (item.tag.edge.type === EDGE_TYPE.FULFILS || item.tag.edge.type === EDGE_TYPE.PROVIDES)
            if (item.tag && item.tag.edge.type === EDGE_TYPE.MODEL) return false
            if (item.tag?.edge?.modelKey !== this.linqset.modelKey) return false
        }else if (item instanceof INode) {
            if (this.linqset.isOrganisationModel()) {
                if (item.tag.model) return false
                if (item.tag.node.superType === NODE_SUPERTYPE.ROLE || item.tag.node.superType === NODE_SUPERTYPE.SYSTEM) {
                    if (this.graphComponent.graph.edgesAt(item, AdjacencyTypes.INCOMING).size > 0) return false
                    if (item.tag.node.insights.find(i => i.modelKey !== ORGANISATION_MODEL_KEY)) return false
                }
            }else {
                if (item.tag.model) return false

                if (item.tag.node && item.tag.node.external) {
                    const eo = (this.graphComponent.graph.edgesAt(item, AdjacencyTypes.OUTGOING).find(e => e.tag && e.tag.edge.type === EDGE_TYPE.MODEL))
                    if (eo) return false
                }
            }
        }
        return true
    }

    private processDeletedSelection(selection:IModelItem[]):IModelItem[] {
        const {graph} = this.graphComponent
        // need to do remove outside of loop as get concurrency exception
        const itemsToDelete:IModelItem[] = []
        let notify:IModelItem[] = []
        const result = selection.filter(i => this.isAllowedToDelete(i))
        result.forEach(i => {
            if (i instanceof INode) {
                if (i.tag.model) {
                    // deleting model node so delete attached info
                    const edges = graph.edgesAt(i, AdjacencyTypes.OUTGOING)
                    edges.forEach(e => {
                        if (!result.find(n => n === e.targetNode)) {
                            itemsToDelete.push(e.targetNode)
                            // need to notifiy linqset changes of join edges being deleted
                            notify = notify.concat(graph.edgesAt(e.targetNode, AdjacencyTypes.OUTGOING).toArray())
                        }
                    })
                }else if (i.tag.node.external && i.tag.node.superType === NODE_SUPERTYPE.INFORMATION && i.tag.node.modelKey !== this.linqset.modelKey) {
                    // deleting external info so delete model nodes
                    const edges = graph.edgesAt(i, AdjacencyTypes.INCOMING)
                    edges.forEach(e => {
                        if (!result.find(n => n === e.sourceNode)) itemsToDelete.push(e.sourceNode)
                    })
                    // need to notifiy linqset changes of join edges being deleted
                    //graph.edgesAt(i, AdjacencyTypes.OUTGOING).forEach(ie => notify.push(ie))
                }
                notify = notify.concat(graph.edgesAt(i, AdjacencyTypes.ALL).toArray())
            }else if (i instanceof IEdge && i.sourceNode.tag && i.sourceNode.tag.model) {
                if (!result.find(n => n === i.sourceNode)) itemsToDelete.push(i.sourceNode)
                if (!result.find(n => n === i.targetNode)) itemsToDelete.push(i.targetNode)
            }
        })
        itemsToDelete.forEach(n => {
            graph.remove(n)
        })

        return result.concat(itemsToDelete).concat(notify)
    }

    private findExistingNodeForName(nodes:LinqsetNode[], node:INode, name:string) {
        return nodes.find(n => {
            if (n.superType !== node.tag.node.superType) return false
            if (this.linqset.workspaceKey && n.projectKey && n.projectKey !== this.linqset.workspaceKey) return false
            if (n._key === node.tag.node._key) return false
            if (n.name && (n.name.toLowerCase() === name.toLowerCase())) return true
            return false
        })
    }

    private changeExistingNode(node:INode, newNode:LinqsetNode) {
        // first delete current linqset node
        this.graphProcessingService.createDeletedLinqsetNodesEvent([node.tag.node], true)

        // change current node to point to selected linqset node
        node.tag.node = newNode
        if (!this.linqset.nodes.find(n => n._key === newNode._key))
            this.linqset.nodes.push(newNode)

        // reassign edges
        try {
            this.graphProcessingService.createAddedItemsEvent(this.graphComponent.graph.edgesAt(node).toArray(), false)
        }catch(err) {
            // ignore
        }
    }

    // the canvas changes size when side panels open/close or the window resizes. yFiles keeps the top-left fixed,
    // which leaves the model off to one side. If the whole model was in view, refit it; otherwise keep the same
    // point in the centre so a user's zoomed-in position isn't lost
    private handleSizeChanged = (evt:SizeChangedEventArgs) => {
        const gc = this.graphComponent
        const oldSize = evt.oldSize
        if (!gc || this.clearingGraph || oldSize.width <= 0 || oldSize.height <= 0 || gc.size.width <= 0 || gc.size.height <= 0) return

        const oldViewport = new Rect(gc.viewPoint, new Size(oldSize.width/gc.zoom, oldSize.height/gc.zoom))
        const content = gc.contentBounds
        if (content && !content.isEmpty && oldViewport.contains(content.topLeft) && oldViewport.contains(content.bottomRight)) {
            gc.fitGraphBounds()
            return
        }

        const centre = oldViewport.center
        gc.viewPoint = new Point(centre.x - gc.size.width/(2*gc.zoom), centre.y - gc.size.height/(2*gc.zoom))
    }

    private handleMouseWheel(e:PointerEventArgs, sender:GraphComponent) {
        if (this.readOnly) return
        const currentItem = this.graphComponent.selection.last()

        const altKey = e.modifiers === ModifierKeys.ALT
        const ctrlKey = e.modifiers === ModifierKeys.CONTROL

        if (!(currentItem instanceof INode) || !altKey || !currentItem || !currentItem.tag || !currentItem.tag.node) {
            sender.mouseWheelBehavior = ctrlKey?MouseWheelBehaviors.SCROLL:MouseWheelBehaviors.ZOOM
            return
        }

        sender.mouseWheelBehavior = MouseWheelBehaviors.NONE;
        // we used to switch types or roles/systems on wheel change, but removing as too dangerous after discussion on 16/09/2024
        /*const wheelEvent = e.originalEvent as WheelEvent

        if (currentItem.tag.node.superType === NODE_SUPERTYPE.SYSTEM || currentItem.tag.node.superType === NODE_SUPERTYPE.ROLE) {
            const currentNode = currentItem.tag.node
            const possibleNodes = this.repoService.organisationLinqset.nodes.filter(n => n.superType === currentNode.superType)
            if (possibleNodes.length > 0) {
                let currentIndex = possibleNodes.findIndex(t => t._key === currentNode._key)

                var newIndex = wheelEvent.deltaY>0?currentIndex+1:currentIndex-1
                if (newIndex <= 0) newIndex = possibleNodes.length -1
                if (newIndex >= possibleNodes.length) newIndex = 0

                currentItem.tag.node = (newIndex === possibleNodes.length) ? currentItem.tag.node : possibleNodes[newIndex];

                this.nodeService.setNodeStyle(currentItem as INode, this.linqset)
                if (!this.linqset.nodes.find(n => n._key === currentItem.tag.node._key))
                    this.linqset.nodes.push(currentItem.tag.node)

                this.editModelGraphEvent.emit(createEdgeChangesEvent(sender.graph.edgesAt(currentItem).toArray()))

                this.labelService.replaceLabel(sender.graph, currentItem, currentItem.tag.node.name||"", LABEL_TYPES.NODE_NAME, this.orientation)

                this.linqsetNodeChangedEvent.emit(currentItem)
                this.emitChangeEvent(currentItem, {type:currentItem.tag.node.type}, true)
            }
        }else {
            const currentType = currentItem.tag.node.type

            const nodeTypes = this.nodeService.getNodeTypesForNode(sender.graph, currentItem as INode)
            let currentIndex = nodeTypes.findIndex(t => t.type === currentType)

            var newIndex = wheelEvent.deltaY>0?currentIndex+1:currentIndex-1
            if (newIndex <= 0) newIndex = nodeTypes.length -1
            if (newIndex >= nodeTypes.length) newIndex = 0

            currentItem.tag.node.type = (newIndex === nodeTypes.length) ? currentItem.tag.node.type : nodeTypes[newIndex].type;

            this.nodeService.setNodeStyle(currentItem as INode, this.linqset)
            this.emitChangeEvent(currentItem, {type:currentItem.tag.node.type}, true)
        }*/
    }

    private handleMouseClick(args:PointerEventArgs, sender:GraphComponent) {
        if (args.changedButtons === PointerButtons.MOUSE_LEFT) {
            this.mouseCurrentLocation = args.location
            this.escapePressed = false
        }
    }

    // returns whether been handled or not
    private handleItemDoubleClicked(item:IModelItem):INode {
        if (item instanceof IEdge) {
            const edge = item as IEdge
            if (edge.tag?.edge && (edge.tag.edge.type === EDGE_TYPE.CREATE || edge.tag.edge.type === EDGE_TYPE.SUPPORT)) {
                // this triggers insertion of info and action nodes
                const newNode = this.insertSupplyChain(edge)
                this.graphService.runLayoutForLinqset(this.graphComponent, this.linqset, true)
                return newNode
            }
        }
        return null
    }

    private runLayout() {
        this.graphService.runLayoutForLinqset(this.graphComponent, this.linqset, true)
    }

    // how a node is moved, from the user's setting: by selecting it first (the standard), or by dragging it straight
    // away with drawing an edge on shift, or the other way round. Set again whenever the setting changes
    private applyNodeMoveOption(inputMode:GraphEditorInputMode, option:NODE_MOVE_OPTION|undefined) {
        const defaults = this.moveDefaults
        if (!defaults) return
        const moveUnselected = inputMode.moveUnselectedItemsInputMode
        const { beginRecognizer, hoverRecognizer } = defaults

        // older profiles may hold it as text
        const value = Number(option ?? NODE_MOVE_OPTION.SELECTED) as NODE_MOVE_OPTION
        if (value === NODE_MOVE_OPTION.SELECTED) {
            inputMode.moveSelectedItemsInputMode.enabled = defaults.selectedEnabled
            moveUnselected.enabled = defaults.unselectedEnabled
            inputMode.createEdgeInputMode.startOverCandidateOnly = defaults.startOverCandidateOnly
            moveUnselected.beginRecognizer = beginRecognizer
            moveUnselected.hoverRecognizer = hoverRecognizer
            moveUnselected.priority = defaults.priority
            return
        }

        // nodes move without being selected first
        inputMode.moveSelectedItemsInputMode.enabled = false
        moveUnselected.enabled = true
        inputMode.createEdgeInputMode.startOverCandidateOnly = false

        // with shift held down it draws an edge, otherwise it moves the node; or the other way round
        const shiftMoves = value !== NODE_MOVE_OPTION.SHIFT_DRAW_EDGE
        moveUnselected.beginRecognizer = (evt, sender) => beginRecognizer(evt, sender) && EventRecognizers.SHIFT_IS_DOWN(evt, sender) === shiftMoves
        moveUnselected.hoverRecognizer = (evt, sender) => hoverRecognizer(evt, sender) && EventRecognizers.SHIFT_IS_DOWN(evt, sender) === shiftMoves

        // before drawing an edge
        moveUnselected.priority = inputMode.createEdgeInputMode.priority - 1
    }

    // the user changed how models are drawn or handled: this canvas takes it on as it is, without a refresh. One that
    // follows the user's direction lays out in the new one; one that keeps its own (a SIPOC, the tutorial) keeps it
    private applyPreferences(change:PreferencesChange) {
        const graphComponent = this.graphComponent
        if (!graphComponent) return
        const changed = changedDrawingPreferences(change.before, change.after)

        if (changed.move && graphComponent.inputMode instanceof GraphEditorInputMode) {
            this.applyNodeMoveOption(graphComponent.inputMode, change.after.nodeModeOption)
        }

        if (!this.linqset || graphComponent.graph.nodes.size === 0) return
        const graph = graphComponent.graph
        const followsUser = !graph.tag?.orientation || graph.tag.orientation === change.before.graphDirection
        const turn = changed.direction && followsUser
        if (!turn && !changed.routing) return

        this._zone.runOutsideAngular(async () => {
            try {
                if (turn) {
                    graph.tag = {...(graph.tag || {}), orientation: change.after.graphDirection}
                    // labels sit beside or below their nodes by the direction
                    this.labelService.removeLabels(graph, true)
                    this.labelService.updateLabelsForGraph(graph, false, this.linqset)
                }
                await this.graphService.runLayoutForLinqset(graphComponent, this.linqset)
            }catch(err) {
                this.exceptionHandler.handleCaughtError(err, COMPONENT, "Apply preferences", "Exception while applying the user's drawing settings", this.userService.getUser(), this.organisationService.organisation, this.linqset?.model)
            }
        })
    }

	private initializeInputModes():GraphEditorInputMode {
		// create the input mode
        const inputMode = new GraphEditorInputMode({
            allowGroupingOperations: true,
            allowCreateNode: false,
            allowEditLabelOnDoubleClick: true,
            allowAddLabel: false,
            ignoreVoidStyles: true,
            allowUndoOperations:true,
            allowClipboardOperations:true,
            movableUnselectedItems: GraphItemTypes.NODE,
            movableSelectedItems: GraphItemTypes.NODE,
            deletableItems: GraphItemTypes.NODE | GraphItemTypes.EDGE,
            selectableItems: GraphItemTypes.ALL | GraphItemTypes.EDGE | GraphItemTypes.BEND,
            clickSelectableItems: GraphItemTypes.NODE | GraphItemTypes.EDGE | GraphItemTypes.LABEL

        })
        inputMode.moveSelectedItemsInputMode.directionalConstraintRecognizer = EventRecognizers.NEVER

        this.createNodeInputMode(inputMode)
        this.createEdgeInputMode(inputMode)

        // how moving nodes starts out, so a change of the user's setting can go back to it
        const moveUnselected = inputMode.moveUnselectedItemsInputMode
        this.moveDefaults = {
            selectedEnabled: inputMode.moveSelectedItemsInputMode.enabled, unselectedEnabled: moveUnselected.enabled,
            startOverCandidateOnly: inputMode.createEdgeInputMode.startOverCandidateOnly,
            beginRecognizer: moveUnselected.beginRecognizer, hoverRecognizer: moveUnselected.hoverRecognizer, priority: moveUnselected.priority,
        }
        this.applyNodeMoveOption(inputMode, this.userService.getUser()?.preferences?.nodeModeOption)

        // lasso selection
        const lassoSelectionInputMode = inputMode.lassoSelectionInputMode
        lassoSelectionInputMode.finishRadius = 10
        lassoSelectionInputMode.enabled = true

        // Configure navigation
        inputMode.navigationInputMode.autoGroupNodeAlignmentPolicy = NodeAlignmentPolicy.TOP_LEFT;
        inputMode.navigationInputMode.fitContentAfterGroupActions  = false;
        inputMode.navigationInputMode.allowEnterGroup = false;
        inputMode.navigationInputMode.allowExitGroup = false;

        const portDropInputMode = new PortDropInputMode()
        portDropInputMode.showPreview = true
        portDropInputMode.snappingEnabled = false
        portDropInputMode.enabled = true
        portDropInputMode.useLocationForParameter = true
        // allow only for nodes to be the new port owner
        portDropInputMode.isValidPortOwnerPredicate = (portOwner, port) => (portOwner instanceof INode)
        inputMode.portDropInputMode = portDropInputMode

        // Enable auto commit lost focust
        inputMode.editLabelInputMode.textEditorInputMode.autoCommitOnFocusLost = true;

        //inputMode.itemHoverInputMode.enabled = true;
        //inputMode.itemHoverInputMode.hoverItems = GraphItemTypes.NODE | GraphItemTypes.EDGE;

        //inputMode.labelEditableItems = GraphItemTypes.LABEL

        this.graphService.createGridLayout(this.graphComponent)
        return inputMode;
    }

    private setupClipboard() {
        this.graphComponent.clipboard.ignoreEdgesWithOneNode = true
        this.graphComponent.clipboard.independentCopyItems = GraphItemTypes.NODE | GraphItemTypes.EDGE

        this.graphComponent.clipboard.pasteOffset = new Point(
            this.graphComponent.clipboard.pasteOffset.x + 300,
            this.graphComponent.clipboard.pasteOffset.y + 300
        )

        this.graphComponent.clipboard.pasteOffsetIncrement = new Point(
            this.graphComponent.clipboard.pasteOffset.x + 200,
            this.graphComponent.clipboard.pasteOffset.y + 200
        )
    }

    private setFreehandSelection() {
        const inputMode = this.graphComponent.inputMode as GraphInputMode
        const lassoSelectionInputMode = inputMode.lassoSelectionInputMode

        //lassoSelectionInputMode.dragFreeRecognizer = EventRecognizers.MOUSE_LEFT_DRAG
        // never start a segment in free-hand lasso selection
        lassoSelectionInputMode.startSegmentRecognizer = EventRecognizers.NEVER
        lassoSelectionInputMode.endSegmentRecognizer = EventRecognizers.MOUSE_LEFT_DOWN
        lassoSelectionInputMode.dragSegmentRecognizer = EventRecognizers.MOUSE_MOVE
        lassoSelectionInputMode.finishRecognizer = EventRecognizers.MOUSE_LEFT_UP

        //lassoSelectionInputMode.dragFreeRecognizerTouch = EventRecognizers.TOUCH_PRIMARY_DRAG
        // never start a segment in free-hand lasso selection
        lassoSelectionInputMode.startSegmentRecognizerTouch = EventRecognizers.NEVER
        lassoSelectionInputMode.endSegmentRecognizerTouch = EventRecognizers.TOUCH_PRIMARY_DOWN
        lassoSelectionInputMode.dragSegmentRecognizerTouch = EventRecognizers.TOUCH_PRIMARY_DRAG
        lassoSelectionInputMode.finishRecognizerTouch = EventRecognizers.TOUCH_PRIMARY_UP

        lassoSelectionInputMode.enabled = true
    }

    private initializeTooltips(inputMode:GraphInputMode) {

        // Customize the tooltip's behavior to our liking.
        const toolTipInputMode = inputMode.toolTipInputMode
        toolTipInputMode.toolTipLocationOffset = new Point(15, 15)
        toolTipInputMode.delay = TimeSpan.fromMilliseconds(50)
        toolTipInputMode.duration = TimeSpan.fromSeconds(10)

        // hovering a node says what it is and what LINQ has worked out for it
        inputMode.addEventListener('query-item-tool-tip', evt => {
            if (evt.handled || !(evt.item instanceof INode)) return
            const tip = this.nodeTip(evt.item)
            if (!tip) return
            evt.toolTip = renderNodeTip(tip)
            evt.handled = true
        })
    }

    // the card for a node on the canvas: a LINQ node with its insights, or a group of them
    private nodeTip(item:INode):NodeTip|undefined {
        const linqset = this.linqset
        if (!linqset) return undefined
        const tag = item.tag
        const format = tipFormat(getCurrency(linqset.organisation?.currencyCode || DEFAULT_CURRENCY))
        if (tag instanceof GroupNodeTag) return groupNodeTip(tag.name, tag.description, tag.insights, format)
        if (tag?.model) return this.modelTip(tag.model, format)
        const node:LinqsetNode|undefined = tag instanceof LinqsetNodeVisualisation ? tag.node : tag?.node
        if (!node || !node.superType) return undefined
        const insights = getInsightsWithoutCreate(node, linqset)
        const related = node.superType === NODE_SUPERTYPE.ACTION ? linqset.getAssociatedRolesAndSystems(node) : undefined
        const unique = (nodes:LinqsetNode[]) => [...new Set(nodes.map(n => n.name).filter(n => !!n))]
        const tags = Object.values(node.properties?.tagKeys || {}).map((k:any) => findTagForOrganisation(linqset.organisation, k)).filter(t => !!t).map(t => ({group: t.group.name, tag: t.tag.name}))
        const modelName = node.modelKey && node.modelKey !== linqset.modelKey && isModelNode(node.superType) ? linqset.organisation.models?.find(m => m._key === node.modelKey)?.name : undefined
        return linqsetNodeTip(node, insights, {format, modelName, roles: related && unique(related.roles), systems: related && unique(related.systems), tags, defaultHours: defaultCapacityHours(linqset.organisation)})
    }

    // a model's card: its insights in the workspace being looked at, or its current state where the project hasn't
    // worked them out
    private modelTip(model:ModelDetails, format:TipFormat):NodeTip {
        const workspaceKey = this.linqset.workspaceKey || CURRENT_STATE_PROJECT_KEY
        const insights = model.insights?.find(i => i.projectKey === workspaceKey) || model.insights?.find(i => i.projectKey === CURRENT_STATE_PROJECT_KEY)
        const tagNames = this.linqset.organisation?.modelTags?.tags || []
        const tags = (model.tagKeys || []).map(k => tagNames.find(t => t._key === k)?.name).filter(n => !!n)
        const project = workspaceKey === CURRENT_STATE_PROJECT_KEY ? undefined
            : model.projectKey !== CURRENT_STATE_PROJECT_KEY ? 'new' : modelChangedInProject(model, workspaceKey) ? 'changed' : undefined
        return modelNodeTip(model, insights, {format, tags, project})
    }

    private insertSupplyChain(edge:IEdge):INode {
        if (!edge?.tag?.edge) return null

        const targetLinqsetEdge = edge.tag.edge as LinqsetEdge
        const edgeType = targetLinqsetEdge.type;
        const {graph} = this.graphComponent

        const startPoint = edge.sourcePort.location
        const endPoint = edge.targetPort.location

        this.preventProcessGraph = true
        try {
            if (edgeType === EDGE_TYPE.CREATE)
            {
                // action creates info
                const infoNode = this.nodeService.createNewNodeAt(graph, NODE_TYPE.INFORMATION, this.linqset, new Point(startPoint.x, startPoint.y+30))
                const actionNode = this.nodeService.createNewNodeAt(graph, NODE_TYPE.ACTION, this.linqset, new Point(endPoint.x, endPoint.y-70))

                this.edgeService.createNewEdge(graph, this.linqset, infoNode, actionNode)
                this.edgeService.createNewEdge(graph, this.linqset, actionNode, edge.targetNode)
                this.preventProcessGraph = false
                const candidate = new PortCandidate(infoNode, FreeNodePortLocationModel.CENTER)
                graph.setEdgePorts(edge, edge.sourcePort, candidate.createPort(this.graphComponent.inputModeContext))

                this.graphService.setSelected(this.graphComponent, infoNode, true);
                return infoNode
            }else if (edgeType === EDGE_TYPE.SUPPORT)
            {
                // information supports action or outcome
                const infoNode = this.nodeService.createNewNodeAt(graph, NODE_TYPE.INFORMATION, this.linqset, new Point(endPoint.x, endPoint.y-70))
                const actionNode = this.nodeService.createNewNodeAt(graph, NODE_TYPE.ACTION, this.linqset, new Point(startPoint.x, startPoint.y+40))

                this.edgeService.createNewEdge(graph, this.linqset, actionNode, infoNode)
                this.edgeService.createNewEdge(graph, this.linqset, infoNode, edge.targetNode)
                this.preventProcessGraph = false
                const candidate = new PortCandidate(actionNode, FreeNodePortLocationModel.CENTER)
                graph.setEdgePorts(edge, edge.sourcePort, candidate.createPort(this.graphComponent.inputModeContext))

                this.graphService.setSelected(this.graphComponent, actionNode, true);
                return actionNode
            }
        }catch(err) {
            console.error(err)
        }finally {
            this.preventProcessGraph = false
        }
        return null
    }

    private canInsertSupplyChain(node:INode, edge:IEdge) {
        return (isSupplyChain(node.tag?.node?.superType) && (edge.tag?.edge?.type === EDGE_TYPE.CREATE || edge.tag?.edge?.type === EDGE_TYPE.SUPPORT))
    }

    private onDragOverHandler(evt:InputModeEventArgs, nodeDropInputMode:NodeDropInputMode) {
        const hits = this.graphComponent.graphModelManager.hitElementsAt(nodeDropInputMode.pointerPosition)
        this.graphComponent.highlightIndicatorManager.items.clear()

		const hit = hits?.first()
		if(!hit) {
			return;
		}

        const draggedNode = nodeDropInputMode.dropData as INode

		if ((hit instanceof INode && this.graphService.canConnectNodes(this.linqset, this.graphComponent.graph, draggedNode, hit)) || (hit instanceof IEdge && this.canInsertSupplyChain(draggedNode, hit))) {
            this.graphComponent.highlightIndicatorManager.items.add(hit)
        }
	}

    private createNodeInputMode(inputMode: GraphEditorInputMode) {
        // Configure drag & drop
        const nodeDropInputMode = inputMode.nodeDropInputMode;
        // by default the mode available in GraphEditorInputMode is disabled, so first enable it
        nodeDropInputMode.enabled = true;
        nodeDropInputMode.showPreview = true;
        nodeDropInputMode.highlightDropTarget = true

        nodeDropInputMode.allowNonGroupNodeAsParent = true;
        nodeDropInputMode.snappingEnabled = false

        const _this = this

        nodeDropInputMode.isValidParentPredicate = function(targetNode) {
            if (!targetNode) return true
            if(!nodeDropInputMode.dropData.tag || !targetNode.tag) {
                return false;
            }

            return _this.graphService.canConnectNodes(_this.linqset, _this.graphComponent.graph, nodeDropInputMode.dropData, targetNode)
        };

        nodeDropInputMode.itemCreator = (
            context,
            graph,
            dropData,
            dropTarget,
            dropLocation
        ) => {
            this.graphComponent.highlightIndicatorManager.items.clear()
            const hits = this.graphComponent.graphModelManager.hitElementsAt(nodeDropInputMode.pointerPosition, context)

      		const hit = hits?.first()
            let newNode:INode

            // Drop on another node, OR just add to canvas.
            if (hit !== null) {
                const {graph} = this.graphComponent

                if (hit instanceof IEdge && this.canInsertSupplyChain(dropData, hit)) {
                    newNode = this.insertSupplyChain(hit as IEdge)
                    this.runLayout()
                }else if (hit instanceof INode && this.graphService.canConnectNodes(this.linqset, this.graphComponent.graph, dropData, hit)) {
                    // create a new node. if we are in a project and this is a new node (does not have a key) then set project id so will be associated with this project
                    const linqsetNode = dropData.tag.node._key?dropData.tag.node: this.nodeService.createLinqsetNode(dropData.tag.node.type, this.linqset)
                    newNode = this.nodeService.createNodeAt(this.linqset, graph, linqsetNode, UUID(), dropLocation)

                    this.graphService.createNewEdge(this.graphComponent, this.linqset, newNode, hit)
                    this.runLayout()
                }
            }else {
                // create a new node. if we are in a project and this is a new node (does not have a key) then set project id so will be associated with this project
                const linqsetNode = dropData.tag.node._key?dropData.tag.node: this.nodeService.createLinqsetNode(dropData.tag.node.type, this.linqset)
                newNode = this.nodeService.createNodeAt(this.linqset, graph, linqsetNode, UUID(), dropLocation)
            }

            return newNode
        }
    }

    private createEdgeInputMode(inputMode: GraphEditorInputMode) {
        const createEdgeInputMode = inputMode.createEdgeInputMode
        //createEdgeInputMode.allowCreateBend = false
        createEdgeInputMode.allowSelfLoops = false
        createEdgeInputMode.allowEdgeToEdgeConnections = false
        createEdgeInputMode.useHitItemsCandidatesOnly = true
        inputMode.createEdgeInputMode.startOverCandidateOnly = true
        inputMode.createEdgeInputMode.directionalConstraintRecognizer = EventRecognizers.NEVER      // turn off edge snapping, default is hold shift

        createEdgeInputMode.cancelRecognizer = (evt, sender) =>
            EventRecognizers.MOUSE_LEFT_CLICK(evt, sender) || EventRecognizers.ESCAPE_DOWN(evt, sender)

        createEdgeInputMode.edgeCreator = (
            context:IInputModeContext,
            graph:IGraph,
            sourceCandidate:IPortCandidate,
            targetCandidate:IPortCandidate,
            dummyEdge:IEdge
        ) => {
            return this.graphService.edgeCreator(this.graphComponent, this.linqset, context, sourceCandidate, targetCandidate, dummyEdge)
        }
    }

    private initializeStyling() {
        this.graphService.initializeStyling(this.graphComponent)
        this.graphService.createPortHandler(this.graphComponent, this.linqset)
	}

    private configureContextMenu(): void {
        const inputMode = this.graphComponent.inputMode as GraphEditorInputMode

        // Add an event listener that populates the context menu according to the hit elements, or cancels showing a menu.
        this.listen(inputMode, 'populate-item-context-menu', (evt) =>
        {
            this.populateContextMenu(this.graphComponent, this.linqset, evt)
        })
    }

        /**
     * Populates the context menu based on the item the mouse hovers over.
     * @param graphComponent The given graphComponent
     * @param args The event args.
     */
    private populateContextMenu(graphComponent:GraphComponent, linqset:Linqset, args: PopulateItemContextMenuEventArgs<IModelItem>): void {
        if (args.handled) return
        if (!this.contextMenu) {
            args.showMenu = false
            return
        }
        args.showMenu = true

        const {graph} = graphComponent
        const location = new Point(args.queryLocation.x, args.queryLocation.y)

        const node = (args.item instanceof INode) ? args.item : null
        // If the cursor is over a node select it
        this.graphService.updateSelection(graphComponent, node)

        const inputMode = graphComponent.inputMode as GraphEditorInputMode
        // Create the context menu items
        if (graphComponent.selection.nodes.size > 0) {
            if (linqset.isOrganisationModel() && node && node.tag.model) {
                const filter = createInsightsFilter(linqset.workspaceKey)
                filter.modelKeys = [node.tag.model._key]
                const modelLinqset = createLinqset(linqset.organisation, filter)

                args.contextMenu = []
                if (node.tag.model.role === USER_ROLE.ADMIN) {
                    args.contextMenu.push({
                        label: 'Edit model details',
                        action: () => {
                            this.functionService.executeFunction(FUNCTION.EDIT_MODEL_DETAILS, undefined, modelLinqset)
                        }
                    })
                }
                if (canDeleteModel(linqset.organisation, node.tag.model, linqset.project)) {
                    args.contextMenu.push({
                        label: 'Delete model',
                        action: () => {
                            this.functionService.executeFunction(FUNCTION.DELETE_MODEL, undefined, modelLinqset)
                        }
                    })
                }
            }else if (!this.readOnly) {
                args.contextMenu = this.nodeService.nodeSuperTypes.filter(nt => canConnect(node.tag.node.type, nt.nodeTypes[0].type)).map(nt => {
                    return {
                        label: `Add ${nt.nodeTypes[0].name} node`,
                        action: () => {
                            this.graphService.addNodeToExistingNode(graphComponent, linqset, node, nt.nodeTypes[0].type)
                        }
                    }
                })

                if (graphComponent.selection.nodes.find(n => n.tag?.node && n.tag.node.superType === NODE_SUPERTYPE.INFORMATION)) {
                    args.contextMenu.push({
                        label: 'Add system to selected info nodes',
                        action: () => {
                            const infoNodes = graphComponent.selection.nodes.filter(n => n.tag?.node && n.tag.node.superType === NODE_SUPERTYPE.INFORMATION).toArray()
                            this.graphService.addNodeToNodes(graphComponent, linqset, infoNodes, this.repoService.organisationLinqset.nodes.filter(n => n.superType === NODE_SUPERTYPE.SYSTEM), NODE_TYPE.SYSTEM)
                        }
                    })
                }
                if (graphComponent.selection.nodes.find(n => n.tag?.node && n.tag.node.superType === NODE_SUPERTYPE.ACTION)) {
                    args.contextMenu.push({
                        label: 'Add system to selected action nodes',
                        action: () => {
                            const actionNodes = graphComponent.selection.nodes.filter(n => n.tag?.node && n.tag.node.superType === NODE_SUPERTYPE.ACTION).toArray()
                            this.graphService.addNodeToNodes(graphComponent, linqset, actionNodes, this.repoService.organisationLinqset.nodes.filter(n => n.superType === NODE_SUPERTYPE.SYSTEM), NODE_TYPE.SYSTEM)
                        }
                    })
                    args.contextMenu.push({
                        label: 'Add role to selected action nodes',
                        action: () => {
                            const actionNodes = graphComponent.selection.nodes.filter(n => n.tag?.node && n.tag.node.superType === NODE_SUPERTYPE.ACTION).toArray()
                            this.graphService.addNodeToNodes(graphComponent, linqset, actionNodes, this.repoService.organisationLinqset.nodes.filter(n => n.superType === NODE_SUPERTYPE.ROLE), NODE_TYPE.ROLE)
                        }
                    })
                }


                if (node && node.tag.node && isSupplyChain(node.tag.node.superType)) {
                    args.contextMenu.push({
                        label: 'Clone node',
                        action: () => {
                            this.graphService.cloneNode(graphComponent, linqset, node)
                        }
                    })
                    args.contextMenu.push({
                        label: 'Select upstream',
                        action: (ev) => this.graphService.selectUpstream(graphComponent, ev, node)
                    })
                    args.contextMenu.push({
                        label: 'Select downstream',
                        action: (ev) => this.graphService.selectDownstream(graphComponent, ev, node)
                    })
                    args.contextMenu.push({
                        label: 'Select connected',
                        action: (ev) => this.graphService.selectConnected(graphComponent, graphComponent.selection.nodes.toArray())
                    })

                    args.contextMenu.push({
                        label: 'Filter selected nodes',
                        action: () => {
                            this.filterService.setINodes(graphComponent.selection.nodes.toArray())
                            this.filterService.applyFilter(linqset, graphComponent)
                        }
                    })
                }
                args.contextMenu.push({
                    label: 'Cut',
                    action: () => inputMode.cut(),
                    //icon: `url("${iconCut}")`
                })
                args.contextMenu.push({
                    label: 'Copy',
                    action: () => inputMode.copy(),
                    //icon: `url("${iconCopy}")`
                })
                args.contextMenu.push({
                    label: 'Delete',
                    action: () => inputMode.deleteSelection(),
                    //icon: `url("${iconDelete}")`
                })
            }
        } else if (!this.readOnly) {
            let addItems
            if (!linqset.isOrganisationModel()) {
                addItems = [
                    {
                        label: 'Add action',
                        action: () => this.nodeService.createNewNodeAt(graph, NODE_TYPE.ACTION, linqset, location)
                    },
                    {
                        label: 'Add information',
                        action: () => this.nodeService.createNewNodeAt(graph, NODE_TYPE.INFORMATION, linqset, location)
                    },
                    {
                        label: 'Add system',
                        action: () => this.nodeService.createNewNodeAt(graph, NODE_TYPE.SYSTEM, linqset, location)
                    },
                    {
                        label: 'Add role',
                        action: () => this.nodeService.createNewNodeAt(graph, NODE_TYPE.ROLE, linqset, location)
                    },
                ]
            }else {
                addItems = [
                    {
                        label: 'Add person',
                        action: () => this.nodeService.createNewNodeAt(graph, NODE_TYPE.PERSON, linqset, location)
                    },
                    {
                        label: 'Add team',
                        action: () => this.nodeService.createNewNodeAt(graph, NODE_TYPE.TEAM, linqset, location)
                    }
                ]
            }

            // no node has been hit
            args.contextMenu = [
                ...addItems,
                { label: 'Select all', action: () => inputMode.selectAll() },
                { label: 'Paste', action: () =>
                    inputMode.pasteAtLocation(args.queryLocation),
                    //icon: `url("${iconPaste}")`,
                    disabled: graphComponent.clipboard.isEmpty
                }
            ]
        }
    }
}

